import React, { useEffect, useRef, useState } from "react";
import { apiService } from "../services/apiService";
import ScoreboardThemePicker from "./ScoreboardThemePicker";
import {
  normalizeScoreboardTheme,
  scoreboardThemeName,
} from "../constants/scoreboardThemes";

export default function ScoreboardAppearanceEditor({ game, clubId }) {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [clubTheme, setClubTheme] = useState("modern-dark");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [reload, setReload] = useState(0);
  const epoch = useRef(0);
  const gameId = game?.id;
  const link = game?.link;
  useEffect(() => {
    const ticket = ++epoch.current;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    setLoading(true);
    setLoaded(false);
    setSaving(false);
    setError("");
    setMessage("");
    apiService
      .request(
        gameId
          ? "/public/scoreboard-theme"
          : `/settings/specific/${clubId || ""}`,
        {
          method: "POST",
          body: JSON.stringify(
            gameId ? { link } : { keys: ["scoreboard_theme"] }
          ),
          signal: controller.signal,
          skipOfflineQueue: true,
        }
      )
      .then((result) => {
        if (ticket !== epoch.current) return;
        if (!result.success) throw new Error("Could not load saved design.");
        const theme = gameId
          ? result.appearance.override == null
            ? null
            : normalizeScoreboardTheme(result.appearance.override)
          : normalizeScoreboardTheme(result.settings.scoreboard_theme);
        setSaved(theme);
        setDraft(theme);
        setClubTheme(
          normalizeScoreboardTheme(gameId ? result.appearance.club_theme : theme)
        );
        setLoaded(true);
      })
      .catch((err) => {
        if (ticket === epoch.current) setError(err.message);
      })
      .finally(() => {
        clearTimeout(timeout);
        if (ticket === epoch.current) setLoading(false);
      });
    return () => {
      epoch.current = ticket + 1;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [gameId, link, clubId, reload]);
  // A club default can change in another tab while this game's editor is open.
  // Refresh its label without overwriting an unsaved per-game selection.
  useEffect(() => {
    if (!gameId || !loaded) return;
    let active = true;
    const controllers = new Set();
    const refreshDefault = async () => {
      if (document.hidden) return;
      const controller = new AbortController();
      controllers.add(controller);
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const result = await apiService.request("/public/scoreboard-theme", {
          method: "POST",
          body: JSON.stringify({ link }),
          signal: controller.signal,
          skipOfflineQueue: true,
        });
        if (active && result.success)
          setClubTheme(normalizeScoreboardTheme(result.appearance.club_theme));
      } catch (_) {
        /* Keep the last known default while offline. */
      } finally {
        clearTimeout(timeout);
        controllers.delete(controller);
      }
    };
    window.addEventListener("focus", refreshDefault);
    document.addEventListener("visibilitychange", refreshDefault);
    const interval = setInterval(refreshDefault, 30000);
    return () => {
      active = false;
      clearInterval(interval);
      controllers.forEach((controller) => controller.abort());
      window.removeEventListener("focus", refreshDefault);
      document.removeEventListener("visibilitychange", refreshDefault);
    };
  }, [gameId, link, loaded]);
  const save = async () => {
    if (!loaded || saving || loading) return;
    const ticket = epoch.current;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await apiService.request(
        gameId
          ? "/club/game/scoreboard-theme"
          : `/settings/batch/${clubId || ""}`,
        {
          method: "POST",
          skipOfflineQueue: true,
          signal: controller.signal,
          body: JSON.stringify(
            gameId
              ? { game_id: gameId, theme: draft }
              : {
                  settings: [
                    {
                      key: "scoreboard_theme",
                      value: draft,
                      category: "Scoreboard",
                      type: "string",
                    },
                  ],
                }
          ),
        }
      );
      if (ticket !== epoch.current) return;
      if (!result.success)
        throw new Error(
          result.message || "Could not save the design. Please retry."
        );
      setSaved(draft);
      if (result.appearance?.club_theme)
        setClubTheme(result.appearance.club_theme);
      setMessage(
        result.live_notified === false
          ? "Design saved. Connected screens will catch up automatically; the live notification is temporarily unavailable."
          : "Design saved. Connected browsers and updated TV apps switch without interrupting play."
      );
    } catch (err) {
      if (ticket === epoch.current)
        setError(
          err.name === "AbortError"
            ? "The server did not respond in time. Your selection is still here; try Save again when connected."
            : err.message
        );
    } finally {
      clearTimeout(timeout);
      if (ticket === epoch.current) setSaving(false);
    }
  };
  return (
    <section
      className="sb-appearance-editor"
      aria-label={gameId ? "Game scoreboard design" : "Club scoreboard design"}
    >
      <div className="sb-appearance-heading">
        <div>
          <span className="sb-appearance-eyebrow">SAME GAME. YOUR STYLE.</span>
          <h3>Scoreboard design</h3>
          <p>
            {gameId
              ? "A look for this match only. Or let your club’s default take the lead."
              : "Set the default look for every snooker scoreboard in your club."}
          </p>
        </div>
        <span className="sb-appearance-scope">
          {gameId ? "This game" : "Club default"}
        </span>
      </div>
      {loading ? (
        <p role="status">Loading saved design…</p>
      ) : loaded ? (
        <ScoreboardThemePicker
          value={draft}
          onChange={(value) => {
            setDraft(value);
            setMessage("");
          }}
          allowInherit={!!gameId}
          clubTheme={clubTheme}
          disabled={saving}
        />
      ) : (
        <button
          type="button"
          className="sb-appearance-save"
          onClick={() => setReload((value) => value + 1)}
        >
          Retry loading
        </button>
      )}
      {error && (
        <p className="sb-appearance-error" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="sb-appearance-success" role="status">
          {message}
        </p>
      )}
      <div className="sb-appearance-footer">
        <span>
          <strong>
            {draft !== saved ? "Unsaved preview" : "Saved design"} ·{" "}
            {draft == null
              ? `Club default (${scoreboardThemeName(clubTheme)})`
              : scoreboardThemeName(draft)}
          </strong>
          <small>
            Appearance only. Scores, clocks, current break and history stay in
            place.
          </small>
        </span>
        <button
          type="button"
          className="sb-appearance-save"
          disabled={!loaded || loading || saving || draft === saved}
          onClick={save}
        >
          {saving ? "Saving…" : "Save design"}
        </button>
      </div>
      <p className="sb-appearance-note">
        Full-screen snooker scoreboards only. Streaming overlays have their own
        settings. TV apps need the version containing these designs.
      </p>
    </section>
  );
}
